import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useAnalytics } from 'hooks'
import { ChevronDown } from 'lucide-react'
import { useEffect } from 'react'
import { SCHEDULE_TOOL_ACTIVITY_EVENT } from 'modules/creative/assistant-chat-events'
import { useWeeklyQueueStore } from '../store/weekly-queue-store'
import { WeeklyCockpit } from './WeeklyCockpit'

interface WeekFloorProps {
  expanded: boolean
  onToggle: (next: boolean) => void
}

/**
 * The week section at the floor of the new Home: a one-line summary strip
 * that expands in place to the full WeeklyCockpit (week grid, day detail,
 * plan wizard — all reused as-is). Fetches the queue itself so the summary
 * counts render even while the cockpit is collapsed and unmounted.
 * Design: playbook/product/home-creation-moment/DESIGN.md.
 */
export function WeekFloor({ expanded, onToggle }: WeekFloorProps) {
  const { track } = useAnalytics()
  const fetchQueue = useWeeklyQueueStore(s => s.fetchQueue)
  const loaded = useWeeklyQueueStore(s => s.loaded)
  const hasActivePlan = useWeeklyQueueStore(s => s.hasActivePlan)
  const scheduledCount = useWeeklyQueueStore(s => s.scheduledCount)
  const generatingCount = useWeeklyQueueStore(s => s.generatingCount)
  const publishedCount = useWeeklyQueueStore(s => s.publishedCount)
  const pendingApprovalCount = useWeeklyQueueStore(s => s.pendingApprovalCount)
  const items = useWeeklyQueueStore(s => s.items)

  useEffect(() => {
    if (!loaded) void fetchQueue()
    // Initial hydrate only — WeeklyCockpit owns refetching once expanded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the assistant dock moves a post, the week strip behind it must
  // follow live (same contract as the Calendar page).
  useEffect(() => {
    const handleScheduleToolActivity = () => void fetchQueue()
    window.addEventListener(SCHEDULE_TOOL_ACTIVITY_EVENT, handleScheduleToolActivity)
    return () =>
      window.removeEventListener(SCHEDULE_TOOL_ACTIVITY_EVENT, handleScheduleToolActivity)
  }, [fetchQueue])

  const handleToggle = () => {
    const next = !expanded
    track(AnalyticsEvents.Home.HOME_WEEK_FLOOR_TOGGLED, { expanded: next })
    onToggle(next)
  }

  // Expanded, the cockpit's own status header ("3 posts need your approval.")
  // IS the section title — a second "Your week" line above it was pure
  // duplication (Matan, 2026-07-05), so the toggle strip only exists while
  // collapsed and the collapse affordance lives inside the cockpit header.
  // The strip derivations below the return stay off the expanded render path,
  // which is exactly when the queue store churns most.
  if (expanded) {
    return (
      <section>
        <WeeklyCockpit onCollapse={handleToggle} />
      </section>
    )
  }

  // The next post actually going out — scheduled only (a needs_approval slot
  // will NOT publish, promising it as "Next" would be dishonest). Date.parse
  // ordering, not string compare: optimistic reschedules can write a different
  // ISO flavor than the server's UTC-Z strings.
  const nextUp = items
    .filter(i => i.status === 'scheduled' && !!i.scheduledAt)
    .sort((a, b) => Date.parse(a.scheduledAt!) - Date.parse(b.scheduledAt!))
    .find(i => Date.parse(i.scheduledAt!) > Date.now())

  const upcoming = scheduledCount + generatingCount
  const summaryParts: string[] = []
  if (upcoming > 0) summaryParts.push(`${upcoming} scheduled`)
  if (publishedCount > 0) summaryParts.push(`${publishedCount} live this week`)
  if (pendingApprovalCount > 0) summaryParts.push(`${pendingApprovalCount} need approval`)
  const summary = !loaded
    ? ''
    : summaryParts.length > 0
      ? summaryParts.join(' · ')
      : hasActivePlan
        ? 'Nothing scheduled yet'
        : 'Set up your posting week'

  return (
    <section>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        className="group flex w-full items-center justify-between gap-3 rounded-[10px] px-2 py-2 text-left transition-colors hover:bg-bg-2"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="text-[15px] font-semibold tracking-tight text-paper">Your week</span>
          {summary && <span className="truncate text-[13px] text-paper-faint">{summary}</span>}
          {nextUp && (
            <span className="ml-1 flex min-w-0 items-center gap-2 rounded-full border border-line bg-bg-2 py-1 pr-3 pl-1">
              {nextUp.thumbnailUrl ? (
                <img
                  src={nextUp.thumbnailUrl}
                  alt=""
                  className="h-5 w-5 rounded-full object-cover"
                  draggable={false}
                />
              ) : (
                <span className="h-5 w-5 rounded-full bg-bg-3" aria-hidden="true" />
              )}
              <span className="truncate text-[12px] text-paper-dim">
                Next:{' '}
                <span className="text-paper">
                  {new Date(nextUp.scheduledAt!).toLocaleString(undefined, {
                    weekday: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </span>
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className="flex-shrink-0 text-paper-faint group-hover:text-paper"
          aria-hidden="true"
        />
      </button>
    </section>
  )
}
